import React, { useState } from 'react';
import { auth } from '../firebaseConfig';
import { readLocally, mapForForm, EXTRA_LABELS } from '../shared/contractAi';

// "Upload contract": reads the PDF's text layer in the browser (pdf.js), then asks /api/contract-extract (AI) for the
// fields. No AI key / AI error / offline = the built-in no-AI reader. Nothing is saved here: the form fills only
// EMPTY fields, highlights them for review, and the agent still presses Save. No tasks or emails are created.
const loadPdfJs = () => new Promise((resolve, reject) => {
  if (window.pdfjsLib) return resolve(window.pdfjsLib);
  const el = document.createElement('script');
  el.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  el.onload = () => { const lib = window.pdfjsLib; lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; resolve(lib); };
  el.onerror = () => reject(new Error('Could not load the PDF reader.'));
  document.head.appendChild(el);
});
export async function readPdfText(file) {
  const pdfjs = await loadPdfJs();
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  let out = '';
  for (let i = 1; i <= doc.numPages; i += 1) {
    const content = await (await doc.getPage(i)).getTextContent();
    out += `${content.items.map((it) => it.str + (it.hasEOL ? '\n' : ' ')).join('')}\n`;
  }
  return out;
}

export async function extractContract(text, fetchImpl = (typeof fetch !== 'undefined' ? fetch : null)) {
  try {
    const token = auth && auth.currentUser ? await auth.currentUser.getIdToken() : '';
    const res = await fetchImpl('/api/contract-extract', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ text }) });
    const body = await res.json().catch(() => null);
    if (body && body.fields) return { ...body, source: res.ok ? (body.source || 'ai') : 'local' };
  } catch (e) { /* fall through to local reader */ }
  return { ...readLocally(text), source: 'local' };
}

// Generic fill for forms without day-count deadlines: fills empty fields only; extras go to formData.contractDetails.
export function fillEmpty(prev, mapped) {
  const next = { ...prev }; const filled = [];
  Object.entries(mapped.values).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '' && !prev[k]) { next[k] = String(v); filled.push(k); } });
  const extras = Object.fromEntries(Object.entries(mapped.extras).map(([k, v]) => [k, String(v)]));
  if (Object.keys(extras).length) { next.contractDetails = { ...extras, ...(prev.contractDetails || {}) }; filled.push('contractDetails'); }
  return { next, filled };
}

// Highlight filled inputs (by name) inside a form for review; the highlight clears when the agent edits the field.
export function highlightFields(names, root = typeof document !== 'undefined' ? document : null) {
  if (!root) return;
  setTimeout(() => names.forEach((n) => root.querySelectorAll(`[name="${n}"]`).forEach((el) => {
    el.style.background = '#fff6d6'; el.title = 'Filled from the contract: check it';
    el.addEventListener('input', () => { el.style.background = ''; el.title = ''; }, { once: true });
  })), 0);
}

// Editable "From the contract" review box for details the form has no input for (saved with the deal on Save).
export function ContractDetails({ value, onChange }) {
  const entries = Object.entries(value || {});
  if (!entries.length) return null;
  return (
    <div style={{ margin: '0 0 16px', padding: 12, background: '#fff6d6', borderRadius: 6 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>From the contract (check each one)</div>
      {entries.map(([k, v]) => (
        <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 4 }}>
          <span style={{ width: 170 }}>{EXTRA_LABELS[k] || k}</span>
          <input value={v} onChange={(e) => onChange({ ...value, [k]: e.target.value })} style={{ flex: 1, padding: 4 }} />
        </label>
      ))}
    </div>
  );
}

export default function ContractImport({ kind = 'Buyer', onFields, onApply }) {
  const [msg, setMsg] = useState('');
  const [notes, setNotes] = useState([]);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setMsg('Reading the contract...'); setNotes([]);
    try {
      const text = await readPdfText(f);
      if (!text.trim()) { setMsg('No text found: this PDF is probably a scan. Fill the form by hand.'); return; }
      const r = await extractContract(text);
      const mapped = mapForForm(kind, r.fields || {});
      const filled = (onApply ? onApply(mapped, r.fields) : onFields(r.fields, mapped)) || [];
      const how = r.source === 'ai' ? 'AI' : 'basic reader (AI not available)';
      setMsg(Object.keys(r.fields || {}).length ? `Read${r.form ? ` as ${r.form}` : ''} with the ${how}. Filled ${filled.length} empty field${filled.length === 1 ? '' : 's'} (highlighted): check each one, then Save.` : 'Could not recognize this contract. Fill the form by hand.');
      setNotes(r.notes || []);
    } catch (err) { setMsg('Could not read that PDF.'); }
  };
  return (
    <div style={{ margin: '0 0 16px', padding: 12, border: '1px dashed #c9a96e', borderRadius: 6 }}>
      <label style={{ fontWeight: 600 }}>Upload contract (PDF) to fill this form <input type="file" accept="application/pdf" onChange={onFile} /></label>
      {msg && <div style={{ fontSize: 13, marginTop: 6 }}>{msg}</div>}
      {notes.length > 0 && <ul style={{ fontSize: 12, color: '#777', margin: '6px 0 0' }}>{notes.map((n) => <li key={n}>{n}</li>)}</ul>}
    </div>
  );
}
